import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { OpsRefund } from '@popcart/core/v1';
import { api, errorMessage } from '../../api';
import { EmptyState, ErrorState, Loading, Segmented, StatusPill, useNow, useTitle } from '../../ui/bits';
import { Fig, PageHead, RowMenu, Stat } from '../../ui/viz';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { STATE_ART } from '../../lib/voice.js';
import { count, paymentLabel, peso } from '../../lib/format.js';
import { useOpsBoard } from '../queries';
import { DialogActions, MoneyField, Modal, StaleNote, TextField } from '../payments/kit';
import { Dot, Hint } from '../payments/money';

import { manilaStamp } from '../reports/period.js';
import {
  PROVIDER_REF_MAX,
  REFUND_PILL,
  REFUND_REASON,
  createRefundAction,
  findOrder,
  normaliseCode,
  processRefundAction,
  refundsKey,
  sortRefunds,
  sourceLabel,
  type RefundFilter,
} from './lib.js';

type Found = { orderId: string; code: string; refundableCentavos: number | null; payment: string | null };

/**
 * /ops/refunds: the money still to send leads; refunds to send come first in the queue; mark one
 * done with the provider reference (or failed, from the row menu); "Refund an order" from its
 * code (or straight from a customer's order).
 */
export function OpsRefundsPage() {
  useTitle('Refunds');
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<RefundFilter>('all');
  const [processing, setProcessing] = useState<{ refund: OpsRefund; status: 'done' | 'failed' } | null>(null);
  const [refunding, setRefunding] = useState<Found | 'lookup' | null>(null);

  // Arriving from Customers → an order → Refund: open the dialog on that order.
  useEffect(() => {
    const orderId = params.get('order');
    const code = params.get('code');
    if (!orderId || !code) return;
    setRefunding({ orderId, code, refundableCentavos: null, payment: null });
    const next = new URLSearchParams(params);
    next.delete('order');
    next.delete('code');
    setParams(next, { replace: true });
  }, [params, setParams]);

  const list = useInfiniteQuery({
    queryKey: refundsKey(filter),
    queryFn: ({ pageParam }) => api.opsRefunds({ status: filter, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 30_000,
  });
  const pending = useInfiniteQuery({
    queryKey: refundsKey('pending'),
    queryFn: ({ pageParam }) => api.opsRefunds({ status: 'pending', cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 30_000,
  });
  const rows = useMemo(() => sortRefunds(list.data?.pages.flatMap((p) => p.items) ?? []), [list.data]);
  const pendingRows = pending.data?.pages.flatMap((p) => p.items) ?? [];
  const more = pending.hasNextPage ? '+' : '';
  const pendingCount = pending.data ? `${count(pendingRows.length)}${more}` : null;
  const pendingSum = pendingRows.reduce((s, r) => s + r.amountCentavos, 0);
  const oldest = pendingRows.reduce<number | null>((m, r) => {
    const t = Date.parse(r.createdAt);
    return Number.isFinite(t) && (m === null || t < m) ? t : m;
  }, null);
  const now = useNow(60_000);
  const seen = rows.filter((r) => r.status !== 'pending');
  const doneSum = seen.filter((r) => r.status === 'done').reduce((s, r) => s + r.amountCentavos, 0);
  const failed = seen.filter((r) => r.status === 'failed').length;

  return (
    <div className="mn-page">
      <PageHead
        title="Refunds"
        meta={pendingCount === null ? 'Loading' : pendingRows.length ? `${pendingCount} to send` : 'All sent'}
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setRefunding('lookup')}>
            <Icon name="refund" />
            Refund an order
          </button>
        }
      />

      <section aria-label="Queue" className="mn-stats4 dv-rise" style={rise(0)}>
        <Stat label="To send" tone={pendingRows.length ? 'ink' : undefined} value={pending.data ? `${peso(pendingSum)}${more}` : '—'} sub={pending.data ? `${pendingCount} ${pendingRows.length === 1 ? 'refund' : 'refunds'}` : undefined} />
        <Stat label="Oldest waiting" value={oldest === null ? '—' : waited(now - oldest)} sub={oldest === null ? 'Nothing waiting' : 'Since created'} visual={<span className="dv-disc" aria-hidden="true"><Icon name="clock" /></span>} />
        <Stat label="Sent" value={peso(doneSum)} sub={filter === 'all' ? 'In this list' : 'Shown'} />
        <Stat label="Failed" value={count(failed)} sub={filter === 'all' ? 'In this list' : 'Shown'} />
      </section>

      <section className="card dv-card dv-rise" aria-labelledby="rf-list-h" style={rise(1)}>
        <div className="dv-card-h" style={{ flexWrap: 'wrap' }}>
          <h2 id="rf-list-h">{filter === 'all' ? 'All refunds' : `${REFUND_PILL[filter].label} refunds`}</h2>
          <Hint text="Send the money in PayMongo (or by bank), then mark it done with the reference. The customer's order shows it as refunded." />
          <span className="grow" />
          <Segmented<RefundFilter>
            label="Show"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'All' },
              { value: 'pending', label: pendingCount ? `To send · ${pendingCount}` : 'To send' },
              { value: 'done', label: 'Done' },
              { value: 'failed', label: 'Failed' },
            ]}
          />
        </div>
        {list.isError && !list.data ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Loading label="Loading refunds" rows={3} />
        ) : rows.length === 0 ? (
          <EmptyState art={STATE_ART.noTickets} title={filter === 'pending' ? 'No refunds to send' : 'No refunds here'}>
            {filter === 'pending' || filter === 'all' ? 'Catch-weight refunds and yours land here.' : 'Try another filter.'}
          </EmptyState>
        ) : (
          <>
            <StaleNote show={list.isError} what="refunds" />
            <RefundsTable rows={rows} onProcess={(refund, status) => setProcessing({ refund, status })} />
            {list.hasNextPage ? (
              <div className="row">
                <button type="button" className="btn btn-quiet" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage} aria-busy={list.isFetchingNextPage || undefined}>
                  {list.isFetchingNextPage ? 'Loading…' : 'Show more refunds'}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>

      <ProcessDialog value={processing} onClose={() => setProcessing(null)} />
      <RefundOrderDialog value={refunding} knownRefunds={[...rows, ...pendingRows]} onClose={() => setRefunding(null)} onFound={(f) => setRefunding(f)} />
    </div>
  );
}

const rise = (i: number) => ({ ['--i' as string]: i }) as CSSProperties;

/** "3 h", "2 d", "15 min": how long a refund has waited. */
function waited(ms: number) {
  const m = Math.max(0, Math.round(ms / 60_000));
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h`;
  return `${Math.round(h / 24)} d`;
}

const DOT: Record<OpsRefund['status'], 'warn' | 'ok' | 'bad'> = { pending: 'warn', done: 'ok', failed: 'bad' };

function RefundsTable({ rows, onProcess }: { rows: OpsRefund[]; onProcess(r: OpsRefund, status: 'done' | 'failed'): void }) {
  return (
    <div className="table-wrap mn-table">
      <table>
        <thead>
          <tr>
            <th scope="col">Order</th>
            <th scope="col" className="num">
              Amount
            </th>
            <th scope="col">Why</th>
            <th scope="col">Status</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pill = REFUND_PILL[r.status];
            return (
              <tr key={r.id} className={r.status === 'pending' ? 'ops-flag' : r.status === 'failed' ? 'ops-flag-error' : undefined}>
                <td>
                  <span className="mn-who">
                    <Dot tone={DOT[r.status]} title={pill.label} />
                    <span className="stack">
                      <span className="mn-code">{r.code}</span>
                      <span className="t-label">{r.customerName}</span>
                      <span className="mn-cap">
                        {paymentLabel(r.paymentMethod)} · {manilaStamp(r.createdAt)}
                      </span>
                    </span>
                  </span>
                </td>
                <td className="num">
                  <Fig text={peso(r.amountCentavos)} size="sm" animate={false} />
                  <div className="mn-cap">{peso(r.refundableCentavos)} left</div>
                </td>
                <td className="ops-wrap">
                  <span className="t-label">{r.reason ?? '—'}</span>
                  <div className="mn-cap">{sourceLabel(r)}</div>
                </td>
                <td>
                  <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
                  {r.status !== 'pending' ? (
                    <div className="mn-cap">
                      {r.providerRef ? `Ref ${r.providerRef} · ` : ''}
                      {r.processedAt ? manilaStamp(r.processedAt) : ''}
                      {r.processedByName ? ` · ${r.processedByName}` : ''}
                    </div>
                  ) : null}
                </td>
                <td className="num">
                  {r.status === 'pending' ? (
                    <div className="mn-act">
                      <button type="button" className="btn btn-secondary" onClick={() => onProcess(r, 'done')} aria-label={`Mark the ${peso(r.amountCentavos)} refund for ${r.code} done`}>
                        <Icon name="check" />
                        Mark done
                      </button>
                      <RowMenu label={`More for the ${r.code} refund`} items={[{ label: 'Mark failed', icon: 'close', tone: 'danger', onSelect: () => onProcess(r, 'failed') }]} />
                    </div>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProcessDialog({ value, onClose }: { value: { refund: OpsRefund; status: 'done' | 'failed' } | null; onClose(): void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [ref, setRef] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<{ providerRef?: string; note?: string }>({});
  useEffect(() => {
    setRef('');
    setNote('');
    setErrors({});
  }, [value]);

  const save = useMutation({
    mutationFn: (v: { refund: OpsRefund; status: 'done' | 'failed'; providerRef: string; note: string }) => processRefundAction(api, v.refund.id, { status: v.status, providerRef: v.providerRef, note: v.note }),
    onSuccess: (r, v) => {
      if (!r.ok) {
        setErrors(r.errors);
        return;
      }
      toast(v.status === 'done' ? `${peso(v.refund.amountCentavos)} back to ${v.refund.customerName} on ${v.refund.code}.` : `The ${v.refund.code} refund is marked failed.`);
      void qc.invalidateQueries({ queryKey: ['ops', 'refunds'] });
      void qc.invalidateQueries({ queryKey: ['ops', 'payments'] });
      onClose();
    },
  });

  const done = value?.status === 'done';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (value) save.mutate({ ...value, providerRef: ref, note });
  };
  return (
    <Modal open={value !== null} labelledBy="rf-process-title" onClose={onClose}>
      {value ? (
        <form className="stack s24" onSubmit={submit} noValidate>
          <div className="stack s4">
            <h2 id="rf-process-title" className="t-headline">
              {done ? `Mark ${peso(value.refund.amountCentavos)} for ${value.refund.code} done` : `Mark the ${value.refund.code} refund failed`}
            </h2>
            <span className="t-caption muted">
              {done ? `Only once the money has gone back to ${value.refund.customerName} (${paymentLabel(value.refund.paymentMethod)}).` : 'It leaves the queue; create a new refund to try again.'}
            </span>
          </div>
          {done ? (
            <TextField id="rf-process-ref" label="Refund reference" value={ref} onChange={setRef} error={errors.providerRef} maxLength={PROVIDER_REF_MAX} placeholder="ref_… or the bank reference" help="The PayMongo refund id or the bank transfer reference." />
          ) : null}
          <TextField id="rf-process-note" label={done ? 'Note (optional)' : 'What went wrong'} value={note} onChange={setNote} error={errors.note} multiline maxLength={REFUND_REASON.max} />
          {save.isError ? (
            <p className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
              {errorMessage(save.error, "Couldn't save it. Try again.")}
            </p>
          ) : null}
          <DialogActions busy={save.isPending} busyLabel="Saving…" confirm={done ? 'Mark done' : 'Mark failed'} danger={!done} onCancel={onClose} />
        </form>
      ) : null}
    </Modal>
  );
}

function RefundOrderDialog({ value, knownRefunds, onClose, onFound }: { value: Found | 'lookup' | null; knownRefunds: OpsRefund[]; onClose(): void; onFound(f: Found): void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const board = useOpsBoard();
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState<string | undefined>();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ amount?: string; reason?: string }>({});
  const amountRef = useRef<HTMLInputElement>(null);
  const order = value && value !== 'lookup' ? value : null;

  useEffect(() => {
    if (value === 'lookup') {
      setCode('');
      setCodeErr(undefined);
    }
    setAmount('');
    setReason('');
    setErrors({});
  }, [value === 'lookup' ? 'lookup' : value?.orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = useMutation({
    mutationFn: (v: { order: Found; form: { amount: string; reason: string } }) => createRefundAction(api, v.order.orderId, v.form, v.order.refundableCentavos),
    onSuccess: (r, v) => {
      if (!r.ok) {
        setErrors(r.errors);
        amountRef.current?.focus();
        return;
      }
      toast(`${peso(r.refund.amountCentavos)} refund for ${v.order.code} is queued. Send it, then mark it done.`);
      void qc.invalidateQueries({ queryKey: ['ops', 'refunds'] });
      onClose();
    },
  });

  const lookup = (e: FormEvent) => {
    e.preventDefault();
    const want = normaliseCode(code);
    if (!want) {
      setCodeErr('Type the order code, like PC-1042.');
      return;
    }
    const f = findOrder(want, { board: board.data, refunds: knownRefunds });
    if (!f) {
      setCodeErr(`${want} isn't on today's board or in past refunds. For an older order, open the customer in Customers and use Refund on the order.`);
      return;
    }
    if (f.payment === 'cod') {
      setCodeErr(`${f.code} was paid in cash. Cash orders can't be refunded here; give a credit from Customers instead.`);
      return;
    }
    setCodeErr(undefined);
    onFound(f);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (order) create.mutate({ order, form: { amount, reason } });
  };

  // Server errors on the amount (400 field amountCentavos: over the refundable) land on the field.
  const serverErr = create.isError ? errorMessage(create.error, "Couldn't create the refund. Try again.") : null;

  return (
    <Modal open={value !== null} labelledBy="rf-new-title" onClose={onClose}>
      {order ? (
        <form className="stack s24" onSubmit={submit} noValidate>
          <div className="stack s4">
            <h2 id="rf-new-title" className="t-headline">
              Refund {order.code}
            </h2>
            <span className="t-caption muted">
              {order.payment ? `${paymentLabel(order.payment)} order. ` : 'E-wallet and card orders only. '}
              {order.refundableCentavos !== null ? `Up to ${peso(order.refundableCentavos)} can still go back.` : 'The API checks how much can still go back.'}
            </span>
          </div>
          <MoneyField id="rf-new-amount" label="Amount to send back" value={amount} onChange={setAmount} error={errors.amount} inputRef={amountRef} />
          <TextField id="rf-new-reason" label="Reason (the customer sees it)" value={reason} onChange={setReason} error={errors.reason} multiline maxLength={REFUND_REASON.max} placeholder="Missing item: 1 kg galunggong" />
          {serverErr ? (
            <p className="pill st-error err-pill" role="alert" style={{ whiteSpace: 'normal' }}>
              {serverErr}
            </p>
          ) : null}
          <DialogActions busy={create.isPending} busyLabel="Creating…" confirm="Create refund" onCancel={onClose} />
        </form>
      ) : (
        <form className="stack s24" onSubmit={lookup} noValidate>
          <div className="stack s4">
            <h2 id="rf-new-title" className="t-headline">
              Refund an order
            </h2>
            <span className="t-caption muted">Find it by its code. The refund waits in the queue until you send the money and mark it done.</span>
          </div>
          <TextField id="rf-new-code" label="Order code" value={code} onChange={setCode} error={codeErr} maxLength={20} placeholder="PC-1042" />
          <DialogActions busy={board.isLoading} busyLabel="Loading orders…" confirm="Find order" onCancel={onClose} />
        </form>
      )}
    </Modal>
  );
}
